import React from 'react';
import { Globe, Database, Server, Cpu } from 'lucide-react';
import { Project } from '../types/project';

export const projectsData: Project[] = [
  {
    id: 'ecommerce-platform',
    title: 'E-Commerce 플랫폼',
    category: 'web',
    shortDescription: 'React와 Node.js를 활용한 풀스택 쇼핑몰. 실시간 재고 관리 및 결제 시스템 연동.',
    fullDescription: `현대적인 UI/UX를 갖춘 풀스택 전자상거래 플랫폼입니다.

주요 기능:
- 실시간 재고 관리 시스템
- 다양한 결제 수단 지원 (카드, 계좌이체, 간편결제)
- 관리자 대시보드를 통한 주문/상품 관리
- 반응형 디자인으로 모바일 최적화
- 검색 및 필터링 기능
- 사용자 리뷰 및 평점 시스템

이 프로젝트를 통해 대규모 트래픽 처리, 결제 시스템 보안, 그리고 사용자 경험 최적화에 대한 경험을 쌓았습니다.`,
    tags: ['React', 'Node.js', 'MongoDB', 'Redis', 'Stripe'],
    techStack: [
      {
        category: 'Frontend',
        technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Redux Toolkit', 'React Query']
      },
      {
        category: 'Backend',
        technologies: ['Node.js', 'Express', 'MongoDB', 'Redis', 'JWT']
      },
      {
        category: 'Payment',
        technologies: ['Stripe API', 'Toss Payments']
      },
      {
        category: 'DevOps',
        technologies: ['Docker', 'AWS EC2', 'Nginx', 'GitHub Actions']
      }
    ],
    images: [
      {
        url: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=800',
        alt: 'E-Commerce 메인 페이지',
        caption: '깔끔한 UI의 메인 쇼핑 페이지'
      },
      {
        url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800',
        alt: '관리자 대시보드',
        caption: '실시간 통계를 보여주는 관리자 대시보드'
      }
    ],
    links: [
      {
        type: 'web',
        url: 'https://example-ecommerce.com',
        label: '웹사이트 방문'
      },
      {
        type: 'github',
        url: 'https://github.com/yourusername/ecommerce',
        label: 'GitHub'
      }
    ],
    icon: React.createElement(Globe, { className: 'w-8 h-8' }),
    featured: true,
    year: '2024'
  },
  {
    id: 'ai-data-analysis',
    title: 'AI 데이터 분석 대시보드',
    category: 'ai',
    shortDescription: '대규모 데이터를 시각화하고 AI 예측 모델 결과를 보여주는 인터랙티브 대시보드.',
    fullDescription: `머신러닝을 활용한 데이터 분석 및 시각화 플랫폼입니다.

주요 기능:
- 실시간 데이터 수집 및 전처리
- TensorFlow 기반 예측 모델
- D3.js를 활용한 인터랙티브 차트
- 자동화된 리포트 생성
- 이상치 탐지 알고리즘
- 맞춤형 대시보드 구성

대용량 데이터 처리와 실시간 분석을 위해 최적화된 아키텍처를 설계했습니다.`,
    tags: ['Python', 'TensorFlow', 'D3.js', 'FastAPI', 'PostgreSQL'],
    techStack: [
      {
        category: 'AI/ML',
        technologies: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'Pandas', 'NumPy']
      },
      {
        category: 'Backend',
        technologies: ['Python', 'FastAPI', 'Celery', 'Redis']
      },
      {
        category: 'Frontend',
        technologies: ['React', 'D3.js', 'Chart.js', 'Material-UI']
      },
      {
        category: 'Database',
        technologies: ['PostgreSQL', 'TimescaleDB']
      }
    ],
    images: [
      {
        url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
        alt: '데이터 대시보드',
        caption: '실시간 데이터 시각화 대시보드'
      },
      {
        url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
        alt: 'AI 모델 분석',
        caption: 'AI 예측 모델 결과 분석'
      }
    ],
    links: [
      {
        type: 'web',
        url: 'https://example-ai-dashboard.com',
        label: '데모 보기'
      },
      {
        type: 'github',
        url: 'https://github.com/yourusername/ai-dashboard',
        label: 'GitHub'
      }
    ],
    icon: React.createElement(Database, { className: 'w-8 h-8' }),
    featured: true,
    year: '2024'
  },
  {
    id: 'realtime-collaboration',
    title: '실시간 협업 툴',
    category: 'web',
    shortDescription: 'Socket.io를 이용한 화상 채팅 및 화이트보드 공유 웹 애플리케이션.',
    fullDescription: `실시간 협업을 위한 올인원 웹 애플리케이션입니다.

주요 기능:
- WebRTC 기반 화상 채팅
- 실시간 화이트보드 공유
- 문서 동시 편집
- 채팅 및 파일 공유
- 세션 녹화 기능
- 사용자 권한 관리

원격 근무 환경에 최적화된 협업 도구로, 낮은 지연시간과 안정적인 연결을 보장합니다.`,
    tags: ['Vue.js', 'WebRTC', 'Socket.io', 'Firebase'],
    techStack: [
      {
        category: 'Frontend',
        technologies: ['Vue.js', 'Vuex', 'TypeScript', 'Tailwind CSS']
      },
      {
        category: 'Real-time',
        technologies: ['Socket.io', 'WebRTC', 'WebSockets']
      },
      {
        category: 'Backend',
        technologies: ['Node.js', 'Express', 'Firebase']
      },
      {
        category: 'Storage',
        technologies: ['Firebase Storage', 'Cloud Firestore']
      }
    ],
    images: [
      {
        url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800',
        alt: '화상 회의',
        caption: 'WebRTC 기반 화상 회의 화면'
      },
      {
        url: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800',
        alt: '화이트보드',
        caption: '실시간 협업 화이트보드'
      }
    ],
    links: [
      {
        type: 'web',
        url: 'https://example-collab.com',
        label: '체험하기'
      },
      {
        type: 'github',
        url: 'https://github.com/yourusername/realtime-collab',
        label: 'GitHub'
      }
    ],
    icon: React.createElement(Server, { className: 'w-8 h-8' }),
    year: '2023'
  },
  {
    id: 'blockchain-wallet',
    title: '블록체인 지갑',
    category: 'blockchain',
    shortDescription: '이더리움 기반의 웹 지갑 서비스. 안전한 트랜잭션 처리와 포트폴리오 관리.',
    fullDescription: `이더리움 블록체인 기반의 안전한 웹 지갑 서비스입니다.

주요 기능:
- 암호화된 개인키 저장
- ERC-20 토큰 지원
- 실시간 가격 정보
- 트랜잭션 히스토리
- NFT 관리 기능
- 하드웨어 지갑 연동

최신 보안 프로토콜과 암호화 기술을 적용하여 사용자 자산을 안전하게 보호합니다.`,
    tags: ['Solidity', 'Web3.js', 'React', 'Ethers.js'],
    techStack: [
      {
        category: 'Blockchain',
        technologies: ['Solidity', 'Web3.js', 'Ethers.js', 'Hardhat']
      },
      {
        category: 'Frontend',
        technologies: ['React', 'TypeScript', 'Redux', 'Styled Components']
      },
      {
        category: 'Backend',
        technologies: ['Node.js', 'Express', 'MongoDB']
      },
      {
        category: 'Security',
        technologies: ['AES Encryption', 'MetaMask Integration']
      }
    ],
    images: [
      {
        url: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800',
        alt: '지갑 대시보드',
        caption: '암호화폐 포트폴리오 대시보드'
      },
      {
        url: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=800',
        alt: '트랜잭션',
        caption: '트랜잭션 처리 화면'
      }
    ],
    links: [
      {
        type: 'web',
        url: 'https://example-wallet.com',
        label: '지갑 열기'
      },
      {
        type: 'github',
        url: 'https://github.com/yourusername/blockchain-wallet',
        label: 'GitHub'
      }
    ],
    icon: React.createElement(Cpu, { className: 'w-8 h-8' }),
    featured: true,
    year: '2023'
  }
];
